import React, { useEffect, useState, memo } from 'react';
import { useParams, useNavigate } from 'react-router';
import { Card, Button, Spin, Alert, Row, Col, Tag, Progress, message } from 'antd';
import { ArrowLeftOutlined, CheckCircleOutlined, ExclamationCircleOutlined, CloseCircleOutlined, LoadingOutlined, ReloadOutlined } from '@ant-design/icons';
import { ApiService } from '../../services/api';
import Markdown from 'react-markdown';

interface EvaluationData {
  id: string;
  origin: string;
  result: string;
  historyId: string;
  evaluation: string;
  totalScore: number;
  informationScore: number;
  detailScore: number;
  logicScore: number;
  grammarScore: number;
  missingInfo: string;
  createTime: number;
  updateTime: number;
  status: string;
  errorMessage?: string;
}

interface Props {}

const EvaluationPage: React.FC<Props> = memo(() => {
  const { historyId } = useParams<{ historyId: string }>();
  const navigate = useNavigate();
  const [evaluationData, setEvaluationData] = useState<EvaluationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reEvaluating, setReEvaluating] = useState(false);

  useEffect(() => {
    if (historyId) {
      fetchEvaluationData();
    }
  }, [historyId]);

  const fetchEvaluationData = async () => {
    if (!historyId) {
      setError('缺少历史记录ID');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await ApiService.getEvaluation({ historyId });
      
      if (response.data.success) {
        setEvaluationData(response.data.data);
        setError(null);
      } else {
        setError(response.data.message || '获取评测数据失败');
      }
    } catch (err: any) {
      setError(err.message || '网络请求失败');
    } finally {
      setLoading(false);
    }
  };

  const handleReEvaluate = async () => {
    if (!historyId) {
      message.error('缺少历史记录ID');
      return;
    }

    try {
      setReEvaluating(true);
      const response = await ApiService.reEvaluate(historyId);
      
      if (response.data.message && response.data.message.includes('未找到对应的历史记录')) {
        message.warning('未找到对应的历史记录，请检查ID是否正确');
        return;
      }
      
      if (response.data.success) {
        message.success('重新评测已启动，正在刷新页面...');
        // 重新加载页面数据
        await fetchEvaluationData();
      } else {
        message.error(response.data.message || '重新评测失败');
      }
    } catch (err: any) {
      message.error(err.message || '重新评测请求失败');
    } finally {
      setReEvaluating(false);
    }
  };

  const getScoreColor = (score: number, maxScore: number = 25) => {
    const percentage = (score / maxScore) * 100;
    if (percentage >= 70) return '#1890ff'; // 蓝色 - 高分
    if (percentage >= 50) return '#faad14'; // 橙色 - 中等分数
    return '#ff4d4f'; // 红色 - 低分
  };

  const getStatusTag = (status: string) => {
    switch (status) {
      case 'SUCCESS':
        return <Tag color="green" icon={<CheckCircleOutlined />}>评测完成</Tag>;
      case 'FAILED':
        return <Tag color="red" icon={<CloseCircleOutlined />}>评测失败</Tag>;
      case 'PENDING':
        return <Tag color="blue" icon={<LoadingOutlined />}>评测中</Tag>;
      default:
        return <Tag color="default">{status}</Tag>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col items-center justify-center py-20">
            <Spin size="large" />
            <p className="mt-4 text-gray-600">正在加载评测结果...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-6">
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2"
          >
            返回
          </Button>
          <Button 
            type="primary"
            icon={<ReloadOutlined />}
            loading={reEvaluating}
            onClick={handleReEvaluate}
            className="flex items-center gap-2"
          >
            {reEvaluating ? '重新评测中...' : '重新评测'}
          </Button>
        </div>
          <Alert
            message="加载失败"
            description={error}
            type="error"
            showIcon
            className="mb-6"
            action={
              <Button size="small" onClick={fetchEvaluationData}>
                重试
              </Button>
            }
          />
        </div>
      </div>
    );
  }

  if (!evaluationData) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-6">
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2"
          >
            返回
          </Button>
          <Button 
            type="primary"
            icon={<ReloadOutlined />}
            loading={reEvaluating}
            onClick={handleReEvaluate}
            className="flex items-center gap-2"
          >
            {reEvaluating ? '重新评测中...' : '重新评测'}
          </Button>
        </div>
          <div className="flex flex-col items-center justify-center py-20">
            <p className="text-gray-500 text-lg">未找到评测数据</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button 
              icon={<ArrowLeftOutlined />} 
              onClick={() => navigate(-1)}
              className="flex items-center gap-2"
            >
              返回
            </Button>
            <h1 className="text-2xl font-bold text-gray-800">评测结果</h1>
          </div>
          <Button 
            type="primary"
            icon={<ReloadOutlined />}
            loading={reEvaluating}
            onClick={handleReEvaluate}
            className="flex items-center gap-2"
          >
            {reEvaluating ? '重新评测中...' : '重新评测'}
          </Button>
        </div>
        <div className="flex items-center justify-between mb-6 p-4 bg-white rounded-lg shadow-sm">
          <div className="flex items-center gap-3">
            {getStatusTag(evaluationData.status)}
            <span className="text-gray-600">
              评测时间: {new Date(evaluationData.createTime).toLocaleString()}
            </span>
          </div>
        </div>

        {/* 总分展示 */}
        <Card className="mb-6">
          <div className="flex items-center justify-center gap-8 py-6">
            <Progress
              type="circle"
              percent={evaluationData.totalScore}
              format={percent => `${percent}分`}
              size={120}
              strokeColor={{
                '0%': '#108ee9',
                '100%': '#87d068',
              }}
            />
            <div className="text-center">
              <h2 className="text-xl font-semibold text-gray-800 mb-2">总分</h2>
              <p className="text-3xl font-bold text-blue-600">{evaluationData.totalScore}/100</p>
            </div>
          </div>
        </Card>

        {/* 详细分数 */}
        <Card title="详细分数" className="mb-6">
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <Card className="text-center hover:shadow-md transition-shadow">
                <div className="text-gray-700 font-medium mb-2">信息保留率</div>
                <div className="text-2xl font-bold mb-3" style={{ color: getScoreColor(evaluationData.informationScore, 60) }}>
                  {evaluationData.informationScore}/60
                </div>
                <Progress 
                  percent={(evaluationData.informationScore / 60) * 100} 
                  showInfo={false}
                  strokeColor={getScoreColor(evaluationData.informationScore, 60)}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card className="text-center hover:shadow-md transition-shadow">
                <div className="text-gray-700 font-medium mb-2">细节完整性</div>
                <div className="text-2xl font-bold mb-3" style={{ color: getScoreColor(evaluationData.detailScore, 20) }}>
                  {evaluationData.detailScore}/20
                </div>
                <Progress 
                  percent={(evaluationData.detailScore / 20) * 100} 
                  showInfo={false}
                  strokeColor={getScoreColor(evaluationData.detailScore, 20)}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card className="text-center hover:shadow-md transition-shadow">
                <div className="text-gray-700 font-medium mb-2">分类与逻辑</div>
                <div className="text-2xl font-bold mb-3" style={{ color: getScoreColor(evaluationData.logicScore, 10) }}>
                  {evaluationData.logicScore}/10
                </div>
                <Progress 
                  percent={(evaluationData.logicScore / 10) * 100} 
                  showInfo={false}
                  strokeColor={getScoreColor(evaluationData.logicScore, 10)}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card className="text-center hover:shadow-md transition-shadow">
                <div className="text-gray-700 font-medium mb-2">语法/专业性</div>
                <div className="text-2xl font-bold mb-3" style={{ color: getScoreColor(evaluationData.grammarScore, 10) }}>
                  {evaluationData.grammarScore}/10
                </div>
                <Progress 
                  percent={(evaluationData.grammarScore / 10) * 100} 
                  showInfo={false}
                  strokeColor={getScoreColor(evaluationData.grammarScore, 10)}
                />
              </Card>
            </Col>
          </Row>
        </Card>



        {/* 缺失信息清单 */}
        {evaluationData.missingInfo && (
          <Card title="改进建议" className="mb-6">
            <div className="bg-yellow-50 p-4 rounded-lg border-l-4 border-yellow-400">
              <div className="flex items-start gap-2">
                <ExclamationCircleOutlined className="text-yellow-500 mt-0.5" />
                <div className="text-gray-700 prose prose-sm max-w-none">
                  <Markdown>{evaluationData.missingInfo}</Markdown>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* 原始内容和生成结果对比 */}
        <Row gutter={16} className="mb-6">
          <Col xs={24} lg={12}>
            <Card title="原始输入" className="h-full">
              <div className="bg-gray-50 p-4 rounded-lg max-h-96 overflow-y-auto">
                <pre className="whitespace-pre-wrap text-sm text-gray-700">
                  {evaluationData.origin}
                </pre>
              </div>
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card title="生成结果" className="h-full">
              <div className="bg-gray-50 p-4 rounded-lg max-h-96 overflow-y-auto">
                <pre className="whitespace-pre-wrap text-sm text-gray-700">
                  {evaluationData.result}
                </pre>
              </div>
            </Card>
          </Col>
        </Row>

        {/* 详细评测报告 */}
        {evaluationData.evaluation && (
          <Card title="原始报告" className="mb-6">
            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="prose prose-sm max-w-none text-gray-700">
                <Markdown>{evaluationData.evaluation}</Markdown>
              </div>
            </div>
          </Card>
        )}

        {/* 错误信息 */}
        {evaluationData.errorMessage && (
          <Alert
            message="评测过程中出现错误"
            description={evaluationData.errorMessage}
            type="error"
            showIcon
            className="mb-6"
          />
        )}
      </div>
    </div>
  );
});

EvaluationPage.displayName = 'EvaluationPage';

export default EvaluationPage;